import { useEffect } from 'react'
import { Alert, BinaryRadioGroup, Link, Text } from '@/ui'
import { InstitutionLogo } from '@/features/user/settings/sections/connected-financial-institutions-card/institution-card/logo'
import { FinancialAccount } from '@/reducers/finances/financialAccountsReducer'
import { PlaidItem } from '@/reducers/finances/plaidItemReducer'
import { useAnalyticsView } from '@/features/amplitude'
import { TypographyStyles } from '@/styles'

export const AccountQuestions = ({
  account,
  institution,
  display,
  isBusinessAccount,
  setIsBusinessAccount,
  hasPersonalTransactions,
  setHasPersonalTransactions,
  hasBusinessTransactions,
  setHasBusinessTransactions,
  isAccountOpenForYear,
  setIsAccountOpenForYear,
}: {
  account: FinancialAccount
  institution?: PlaidItem | null
  display: boolean | undefined
  isBusinessAccount: boolean | undefined
  setIsBusinessAccount: (is: boolean | undefined) => void
  hasPersonalTransactions: boolean | undefined
  setHasPersonalTransactions: (has: boolean | undefined) => void
  hasBusinessTransactions: boolean | undefined
  setHasBusinessTransactions: (has: boolean | undefined) => void
  isAccountOpenForYear: boolean | undefined
  setIsAccountOpenForYear: (is: boolean | undefined) => void
}) => {
  const pageView = useAnalyticsView()

  const currentYear = new Date().getFullYear()

  useEffect(() => {
    pageView('bank account information')
  }, [pageView])

  const renderQ2 = isBusinessAccount !== undefined
  const renderQ3 =
    hasPersonalTransactions !== undefined ||
    hasBusinessTransactions !== undefined

  if (!display) {
    return null
  }

  const handleIsBusinessAccountChange = (value: boolean) => {
    setHasPersonalTransactions(undefined)
    setHasBusinessTransactions(undefined)
    setIsBusinessAccount(value)
  }

  const isQ2Answered =
    hasPersonalTransactions !== undefined ||
    hasBusinessTransactions !== undefined

  return (
    <div className="space-y-6">
      <Text variant={TypographyStyles.H1}>Tell us about this account</Text>
      <Text>
        Your responses let us know if we need to provide additional support to
        set up the account.
      </Text>

      <div className="flex flex-col gap-2 rounded-lg bg-muted p-4">
        {institution && (
          <InstitutionLogo
            institution={institution}
            className="institution-logo"
          />
        )}
        <Text variant={TypographyStyles.H3}>
          {account.name} Account {account.mask}
        </Text>
      </div>

      <div className="max-w-xl space-y-8">
        <BinaryRadioGroup
          label="Is this account used for your business?"
          text="Heard only works with accounts used for your business. This means the account has transactions related to your practice."
          name="isBusinessAccount"
          onChange={handleIsBusinessAccountChange}
          value={isBusinessAccount}
        />

        {isBusinessAccount === false && (
          <Alert variant="warning">
            <Text variant={TypographyStyles.BODY_MD}>
              A business account is required to make the most of Heard. If you
              don’t have one already, you can sign up for one later.
            </Text>
            <Link
              newPage
              href="https://support.joinheard.com/hc/en-us/articles/23407015266967/#open_business_account"
            >
              Learn more →
            </Link>
          </Alert>
        )}

        {renderQ2 && (
          <>
            {isBusinessAccount ? (
              <BinaryRadioGroup
                label="Does it have any personal transactions?"
                text="A personal transaction is any transaction that isn’t for your business. Since Heard only handles business transactions, we need to know this so we can help you remove them later."
                name="hasPersonalTransactions"
                onChange={setHasPersonalTransactions}
                value={hasPersonalTransactions}
              />
            ) : (
              <BinaryRadioGroup
                label="Does it have any business transactions?"
                text="A business transaction is any purchase or income that is related to your practice, like software you use to run your business or income you make from clients."
                name="hasBusinessTransactions"
                onChange={setHasBusinessTransactions}
                value={hasBusinessTransactions}
              />
            )}

            {isQ2Answered &&
              isBusinessAccount === false &&
              hasBusinessTransactions && (
                <Alert variant="warning">
                  <Text variant={TypographyStyles.BODY_MD}>
                    To maintain accurate bookkeeping, we ask that you unlink
                    this personal account, then manually add your business
                    transactions once you have access to the Heard app. Please
                    see the instructions below:
                  </Text>
                  <Link
                    newPage
                    href="https://support.joinheard.com/hc/en-us/articles/19671412059799-How-can-I-stop-sharing-a-financial-account-with-Heard"
                  >
                    1. Unlink your personal account from Heard →
                  </Link>
                  <Link
                    newPage
                    href="https://support.joinheard.com/hc/en-us/articles/23407015266967-Using-a-dedicated-business-bank-account-for-your-practice#business_transactions_personal_account"
                  >
                    2. Manually add business transactions within your personal
                    account →
                  </Link>
                </Alert>
              )}

            {isQ2Answered && isBusinessAccount && hasPersonalTransactions && (
              <Alert>
                <Text variant={TypographyStyles.BODY_MD}>
                  Once you get access to the full Heard app, we’ll ask you to
                  mark these transactions as Personal. This removes them from
                  your business books.
                </Text>
                <Link
                  newPage
                  href="https://support.joinheard.com/hc/en-us/articles/23407015266967-Using-a-dedicated-business-bank-account-for-your-practice"
                >
                  What are the risks of a commingled account? →
                </Link>
              </Alert>
            )}
          </>
        )}

        {renderQ3 && (
          <BinaryRadioGroup
            label={`Has this account been open for all of ${currentYear}?`}
            text="We ask this to ensure all your transactions are accounted for during bookkeeping."
            name="isAccountOpenForYear"
            onChange={setIsAccountOpenForYear}
            value={isAccountOpenForYear}
          />
        )}
      </div>
    </div>
  )
}
